import { useRef } from "react";
import type { AdminGalleryAlbum } from "@tih/shared";
import { useGalleryUpload } from "../../context/GalleryUploadContext";

type Props = {
  album: AdminGalleryAlbum;
  onError: (message: string | null) => void;
  className?: string;
};

export function GalleryPhotosUploadDropzone({ album, onError, className }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const { startUpload, jobs } = useGalleryUpload();

  const albumBusy = jobs.some(
    (j) =>
      j.albumId === album.id &&
      (j.phase === "uploading" || j.phase === "extracting" || j.phase === "processing"),
  );

  function handleUpload(files: FileList | File[]) {
    const list = Array.from(files);
    if (list.length === 0) return;
    onError(null);
    startUpload(album, list);
  }

  return (
    <div
      className={className ?? "admin-gallery-photos-dropzone"}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        if (e.dataTransfer.files.length) handleUpload(e.dataTransfer.files);
      }}
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/*,.zip,application/zip"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) handleUpload(e.target.files);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        className="btn btn-primary"
        disabled={albumBusy}
        onClick={() => fileRef.current?.click()}
      >
        {albumBusy ? "Upload in progress…" : "Upload photos or zip"}
      </button>
      <span className="admin-muted">
        Drag images or a .zip here (up to 500 per zip). Upload progress appears in the toast at
        bottom-right.
      </span>
    </div>
  );
}
